import React, { useCallback } from 'react';
import { bool, func, object, shape, string } from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { get } from 'ravenjs/utils/lodash';

import { actions as authActions } from 'modules/auth';
import { actions as uiActions } from 'modules/ui';
import { MODALS } from 'constants/modals';
import { selectors as userSelectors } from 'modules/user';
import { isPublicRoute } from 'utils/thrservice';
import { APP_PERMISSIONS } from 'constants/permissions';
import { ROOT_PRIVATE_ROUTE, ROOT_PRIVATE_ROUTE_PLATFORM_LITE } from 'constants/routes';
import Acl from 'modules/acl';
import USERS from 'constants/users';

import AccountSection from './AccountSection';
import AccountSwitcherHeaderStyled, {
    AccountSwitcherHeaderInner,
    SectionSlot,
} from './AccountSwitcherHeaderStyled';
import AccountSwitcherList from './AccountSwitcherList';
import ImpersonationSection from './ImpersonationSection';

const AccountSwitcherHeader = ({
    authorized,
    history,
    userCompany,
    openModal,
    closeModal,
    isImpersonatingUser,
    user,
    switchCompany,
}) => {
    const pathname = get(get(history, 'location', {}), 'pathname', '');
    const partnerName = get(userCompany, 'companyName', '');
    const isPrimaryAccount = get(userCompany, 'isPrimaryCompany', false);

    const handleSwitchAccountConfirm = useCallback(
        async selectedCompanyId => {
            if (!selectedCompanyId) {
                return;
            }

            closeModal(MODALS.COMMON);

            await switchCompany(selectedCompanyId);

            if (Acl.check(APP_PERMISSIONS.platformliteCompanyDashboardView)) {
                history.push(ROOT_PRIVATE_ROUTE_PLATFORM_LITE);
            } else {
                history.push(ROOT_PRIVATE_ROUTE);
            }
        },
        [closeModal, switchCompany, history]
    );

    const handleCloseModal = useCallback(() => {
        closeModal(MODALS.COMMON);
    }, [closeModal]);

    const handleSwitchAccount = useCallback(() => {
        openModal(MODALS.COMMON, {
            hasCloseIcon: true,
            isComponent: true,
            hideActions: true,
            hideIcon: true,
            description: (
                <AccountSwitcherList
                    onCancel={handleCloseModal}
                    onConfirm={handleSwitchAccountConfirm}
                    currentPartnerName={partnerName}
                />
            ),
            ModalProps: {
                ContentProps: {
                    style: {
                        maxWidth: '872px',
                        maxHeight: '626px',
                        margin: '20px',
                        borderRadius: '8px',
                    },
                },
            },
            modalProps: {
                headerProps: {
                    display: 'none',
                },
                bodyProps: {
                    padding: '24px',
                },
            },
        });
    }, [openModal, handleCloseModal, handleSwitchAccountConfirm, partnerName]);

    const handleEndImpersonation = useCallback(() => {
        openModal(MODALS.CONFIRM, {
            title: USERS.IMPERSONATION_MODAL.TITLE,
            description: USERS.IMPERSONATION_MODAL.DESCRIPTION,
            actions: [
                {
                    text: USERS.IMPERSONATION_MODAL.BUTTON.CANCEL,
                    color: 'secondary',
                    onClick() {
                        closeModal(MODALS.CONFIRM);
                    },
                },
                {
                    text: USERS.IMPERSONATION_MODAL.BUTTON.OK,
                    color: 'primary',
                    onClick() {
                        closeModal(MODALS.CONFIRM);
                        setTimeout(() => {
                            history.push('/impersonation/end');
                        }, 300);
                    },
                },
            ],
        });
    }, [openModal, closeModal, history]);

    if (isPublicRoute(pathname) || !authorized) {
        return null;
    }

    return (
        <AccountSwitcherHeaderStyled className="accountSwitcherBar">
            <AccountSwitcherHeaderInner>
                <SectionSlot
                    half={isImpersonatingUser}
                    withDivider={isImpersonatingUser}
                    stackedDivider={isImpersonatingUser}
                >
                    <AccountSection
                        partnerName={partnerName}
                        isPrimaryAccount={isPrimaryAccount}
                        onSwitchAccount={handleSwitchAccount}
                    />
                </SectionSlot>
                {isImpersonatingUser && (
                    <SectionSlot half>
                        <ImpersonationSection
                            impersonatedName={get(user, 'fullName', '')}
                            onEndSession={handleEndImpersonation}
                        />
                    </SectionSlot>
                )}
            </AccountSwitcherHeaderInner>
        </AccountSwitcherHeaderStyled>
    );
};

AccountSwitcherHeader.propTypes = {
    authorized: bool,
    history: shape({
        location: shape({
            pathname: string,
        }),
    }),
    userCompany: object,
    openModal: func.isRequired,
    closeModal: func.isRequired,
    isImpersonatingUser: bool,
    user: object,
    switchCompany: func.isRequired,
};

AccountSwitcherHeader.defaultProps = {
    authorized: false,
    userCompany: {},
    history: {},
    isImpersonatingUser: false,
    user: {},
};

const mapStateToProps = createStructuredSelector({
    userCompany: userSelectors.getUserCompany,
});

const mapDispatchToProps = {
    openModal: uiActions.openModal,
    closeModal: uiActions.closeModal,
    switchCompany: authActions.switchCompany.trigger,
};

export { AccountSwitcherHeader as UnwrappedAccountSwitcherHeader };
export default connect(mapStateToProps, mapDispatchToProps)(AccountSwitcherHeader);
